"use client"

import { useState } from "react"
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@ui/shad"
import { FileText, Mic, Upload } from "lucide-react"

import { useBookNotetakerPolicy } from "@/hooks/api/notetaker-policies"
import { useMeetingRecordingUpload } from "@/hooks/use-meeting-recording-upload"
import { blockedBy } from "@/components/notetaker-policy/policy-resolution"
import { PartnerFeatureGate } from "@/components/partners/partner-feature-gate"

import { UploadTranscriptDialog } from "./upload-transcript-dialog"

interface UploadMeetingDropdownProps {
  meetingId: string
  bookId?: string
  hasExistingTranscript?: boolean
}

function UploadMeetingDropdownContent({ meetingId, bookId, hasExistingTranscript }: UploadMeetingDropdownProps) {
  const [transcriptDialogOpen, setTranscriptDialogOpen] = useState(false)
  const { triggerFileSelection, fileInputElement, isUploading } = useMeetingRecordingUpload(meetingId)
  const { data: notetakerPolicy } = useBookNotetakerPolicy(bookId ?? "")

  if (blockedBy(notetakerPolicy, "outside_recordings")) return null

  return (
    <>
      {fileInputElement}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="primary" onClick={(e) => e.stopPropagation()} disabled={isUploading}>
            <Upload />
            {isUploading ? "Uploading..." : "Upload"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={triggerFileSelection}>
            <Mic />
            Upload recording
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setTranscriptDialogOpen(true)}>
            <FileText />
            Upload transcript
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <UploadTranscriptDialog
        open={transcriptDialogOpen}
        setOpen={setTranscriptDialogOpen}
        meetingId={meetingId}
        hasExistingTranscript={hasExistingTranscript}
      />
    </>
  )
}

export function UploadMeetingDropdown(props: React.ComponentProps<typeof UploadMeetingDropdownContent>) {
  return (
    <PartnerFeatureGate feature="notetaker">
      <UploadMeetingDropdownContent {...props} />
    </PartnerFeatureGate>
  )
}
